<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content">
        <section class="intro">
          <p>
            <strong>Google Street View</strong> is how millions of people
            preview a business before they visit. When your interior appears as
            connected 360° photos on Google Maps and your Google Business
            Profile, searchers can walk through your space from Search, Maps, or
            the <strong>Street View app</strong> — without booking an
            appointment first.
          </p>
          <p>
            With Travvir you publish from the app — without a personal Google
            login. Imagery is placed on
            <em>your</em> Google Maps location; <strong>Travvir is the Street
            View publisher</strong> shown on that place.
          </p>
          <p>
            There are two paths. For a <strong>single scan</strong>, choose the
            place on Google Maps, select the scenes, then publish them to Street
            View. For a <strong>360° tour</strong>, choose the place on Google
            Maps and publish the linked tour directly — no separate per-room
            upload.
          </p>
        </section>

        <section class="key-takeaways">
          <h2>Key Takeaways</h2>
          <ul>
            <li>
              Google Street View for businesses means linked indoor 360°
              panoramas on Maps and your Business Profile (“See inside”).
            </li>
            <li>
              Travvir is the Street View publisher at your Maps place — you do
              not connect a personal Google account in the app.
            </li>
            <li>
              Single scan: pick the Google Maps place, select scenes, then
              publish. 360° tour: pick the place and publish the tour directly.
            </li>
            <li>
              Google reviews new imagery; most businesses see it live within
              <strong>24–72 hours</strong> (sometimes longer).
            </li>
            <li>
              Hotspots and branding stay on your Travvir tour; on Google,
              viewers see panoramas and arrow-style room links.
            </li>
            <li>
              Listings with strong 360 content tend to earn more engagement on
              Google — a signal that supports local SEO.
            </li>
          </ul>
        </section>

        <section>
          <h2>What Is Google Street View for Businesses?</h2>
          <p>
            When people talk about putting their business on
            <strong>Google Street View</strong>, they usually mean
            <strong>indoor connected panoramas</strong> tied to a real address —
            not only the outdoor car-camera imagery on the street. Those
            interior spheres appear in Google Maps, Google Search (your Business
            Profile panel), and the Street View app as blue dots or a “See
            inside” experience.
          </p>
          <p>
            Each scene is a full 360° photo. When scenes are linked, viewers tap
            arrows to move room to room — the same navigation pattern users
            already know from outdoor Street View.
          </p>
        </section>

        <section>
          <h2>Why Publish on Google Street View?</h2>
          <p>
            Businesses with virtual tours and 360 photos on Google often see
            stronger profile engagement than listings with flat photos alone.
            That matters because engagement (direction requests, website clicks,
            calls, photo views) feeds local relevance signals.
          </p>
          <p>
            Practically, Street View answers questions static photos cannot: How
            big is the space? Is it accessible? Does it match your brand? A
            clear tour reduces uncertainty and brings in better-qualified
            visits.
          </p>
        </section>

        <section>
          <h2>How Travvir Publishes to Google Street View Today</h2>
          <p>
            Publishing stays inside Travvir. You first choose the matching
            <strong>Google Maps place</strong> (name and pin). Travvir then
            submits the panoramas through its authorized Street View publisher
            account. On Street View, <strong>Travvir appears as the
            publisher</strong> for that place; coverage still sits at your
            address. Google reviews the imagery before it goes live on Maps and
            your Business Profile.
          </p>
        </section>

        <section>
          <h2>How to Publish with Travvir</h2>
          <p>
            Capture on your phone, then publish from Share → Google Street View.
            The product flow is the same as on our
            <a :href="`${mainSiteUrl}/google-streetview`"
              >Google Street View page</a
            >.
          </p>

          <h3>Single scan</h3>
          <p>
            After you capture one scan, choose the place on Google Maps, select
            the 360° scenes to include, then publish them to Street View. Flat
            floor plans or 2D slides are skipped. Accurate pins keep the scan on
            the right building.
          </p>

          <h3>360° tour</h3>
          <p>
            When rooms are already linked as a tour, choose the place on Google
            Maps and publish the tour directly. Travvir submits the connected
            panoramas together — you do not upload each room by hand.
          </p>

          <h3>After you submit</h3>
          <p>
            Travvir usually queues processing in a few minutes. Google then
            reviews for policy and quality. Plan on
            <strong>24–72 hours</strong> before imagery is public on Maps,
            Search, and the Street View app; busy periods can take longer.
          </p>

          <h3>What appears on Google vs in Travvir</h3>
          <p>
            On Google, viewers see your panoramas and Google’s arrow navigation
            between linked scenes. Custom Travvir hotspots, labels, cards, and
            branding are not shown on Street View — keep those on the embeddable
            Travvir tour you share on your website or in listings.
          </p>
        </section>

        <section>
          <h2>Technical Requirements (Google Street View)</h2>
          <ul>
            <li>
              <strong>360° only:</strong> Equirectangular JPEG panoramas (not
              flat photos).
            </li>
            <li>
              <strong>Resolution:</strong> Meet Google’s minimum (about 7.5 MP;
              higher is better — see our
              <a href="/blog/en/360-virtual-tour-image-size"
                >360 image size guide</a
              >).
            </li>
            <li>
              <strong>Location:</strong> Scenes must match a real, publishable
              place; follow Google’s content policies for your category.
            </li>
            <li>
              <strong>Business details:</strong> Accurate Maps place so Google
              can attach panoramas to the right listing. Travvir is the Street
              View publisher at that place.
            </li>
          </ul>
        </section>

        <section>
          <h2>Travvir vs the Street View App Alone</h2>
          <p>
            The free <strong>Street View app</strong> is ideal for a quick
            single photo sphere from your phone. Travvir fits when you need
            several rooms, consistent quality, hotspots on your own hosted tour,
            and <strong>one-click publish</strong> of the full linked set to
            Google. Many businesses use Travvir for the tour they embed on their
            site and publish the same capture to Street View for Maps discovery.
          </p>
        </section>

        <section>
          <h2>Fallback: Manual Upload</h2>
          <p>
            If you ever need a manual path, export equirectangular images from
            Travvir and upload via the Street View app or Google Business
            Profile photos. Direct publish from Travvir is faster for multi-room
            tours and keeps scene links aligned.
          </p>
        </section>

        <section>
          <h2>Best Practices</h2>
          <p>
            Shoot during daylight or with all lights on; avoid mixed color
            temperatures. Start at the entrance customers use. Refresh your tour
            after renovations or seasonal merchandising changes so Maps stays
            trustworthy.
          </p>
        </section>

        <section>
          <h2>Continue Reading</h2>
          <ul>
            <li>
              <a :href="`${mainSiteUrl}/google-streetview`"
                >Publish to Google Street View with Travvir</a
              >
            </li>
            <li>
              <a href="/blog/en/google-street-view-virtual-tour"
                >Google Street View virtual tour overview</a
              >
            </li>
            <li>
              <a href="/blog/en/360-photos-local-seo"
                >360 photos and local SEO</a
              >
            </li>
          </ul>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";
const postData = blogPostsData.find((p) => p.slug === "google-street-view");
const config = useRuntimeConfig();
const mainSiteUrl = config.public.baseUrl;

useBlogSeo({
  title: "Google Street View: Publish a 360 Tour from Travvir [2026] | Travvir",
  description:
    "Publish a single scan or a 360° tour to Google Street View from Travvir. Choose the Google Maps place; Travvir is the publisher at that location.",
  keywords:
    "google street view, publish google street view, 360 tour google maps, google business street view, travvir google street view",
  slug: "google-street-view",
  image: postData?.image,
  postData,
  faqItems: [
    {
      question: "How do I publish a single scan to Google Street View?",
      answer:
        "In Travvir, choose the matching Google Maps place, select the 360° scenes from that scan, then publish. Travvir is the Street View publisher at that place; Google reviews the imagery before it goes live.",
    },
    {
      question: "How do I publish a 360° tour to Street View?",
      answer:
        "Choose the Google Maps place for your business, then publish the linked tour directly from Travvir. You do not upload each room separately. Travvir appears as the publisher on that Maps place.",
    },
    {
      question: "How long until imagery appears on Google Maps?",
      answer:
        "Travvir usually processes your publish queue within minutes. Google’s public review typically takes 24–72 hours, though it can take longer during peak periods.",
    },
    {
      question: "Do Travvir hotspots show on Google Street View?",
      answer:
        "No. Google displays 360° panoramas and arrow navigation between linked scenes. Keep interactive hotspots and branding on your Travvir-hosted tour and embed.",
    },
  ],
});
</script>
